import React, { createContext, useContext, useState, useEffect } from "react";

const GlobalSettingsContext = createContext();

// Default structure for globalSettings
const defaultGlobalSettings = {
  result: [
    {
      invoiceSettings: {
        predefinedNo: {
          count: 0,
          prefix: "REC",
        },
      },
    },
  ],
};

// Function to validate and merge settings with default structure
const validateAndMergeSettings = (settings) => {
  if (!settings || typeof settings !== "object") {
    return defaultGlobalSettings;
  }

  const validated = {
    ...defaultGlobalSettings,
    ...settings,
  };

  // Ensure result exists
  if (!Array.isArray(validated.result) || validated.result.length === 0) {
    validated.result = defaultGlobalSettings.result;
  } else {
    validated.result[0] = {
      ...defaultGlobalSettings.result[0],
      ...validated.result[0],
    };

    if (!validated.result[0].invoiceSettings) {
      validated.result[0].invoiceSettings =
        defaultGlobalSettings.result[0].invoiceSettings;
    } else {
      validated.result[0].invoiceSettings.predefinedNo = {
        ...defaultGlobalSettings.result[0].invoiceSettings.predefinedNo,
        ...validated.result[0].invoiceSettings.predefinedNo,
      };
    }
  }

  return validated;
};

export const GlobalSettingsProvider = ({ children }) => {

  const [globalSettings, setGlobalSettingsState] = useState(() => {
    try {

      if (typeof Storage === "undefined") {
        console.warn("⚠️ localStorage is not available in this environment");
        return defaultGlobalSettings;
      }

      const stored = localStorage.getItem("globalSettings");

      if (
        stored === null ||
        stored === "undefined" ||
        stored === "null" ||
        stored === ""
      ) {
        return defaultGlobalSettings;
      }

      const parsed = JSON.parse(stored);

      const validatedSettings = validateAndMergeSettings(parsed);

      return validatedSettings;
    } catch (error) {
      console.error("❌ Error loading settings from localStorage:", error);
      try {
        localStorage.removeItem("globalSettings");
      } catch (clearError) {
        console.error("❌ Error clearing localStorage:", clearError);
      }
      return defaultGlobalSettings;
    }
  });


  // Custom setter
  const setGlobalSettings = (newSettingsOrUpdater) => {

    try {
      if (typeof newSettingsOrUpdater === "function") {
        setGlobalSettingsState((prevSettings) => {
          const safePrevSettings = prevSettings || defaultGlobalSettings;
          const updatedSettings = newSettingsOrUpdater(safePrevSettings);

          const validatedSettings = validateAndMergeSettings(updatedSettings);

          if (validatedSettings) {
            localStorage.setItem(
              "globalSettings",
              JSON.stringify(validatedSettings)
            );
          }

          return validatedSettings;
        });
      } else {
        const validatedSettings = validateAndMergeSettings(newSettingsOrUpdater);

        setGlobalSettingsState(validatedSettings);

        if (validatedSettings) {
          localStorage.setItem(
            "globalSettings",
            JSON.stringify(validatedSettings)
          );
        } else {
          localStorage.removeItem("globalSettings");
        }
      }
    } catch (error) {
      console.error("❌ Error in setGlobalSettings:", error);
    }
  };


  // Initialize localStorage if it's empty
  useEffect(() => {
    const stored = localStorage.getItem("globalSettings");
    if (!stored || stored === "undefined" || stored === "null") {
      localStorage.setItem("globalSettings", JSON.stringify(globalSettings));
    }
  }, []);


  return (
    <GlobalSettingsContext.Provider value={{ globalSettings, setGlobalSettings }}>
      {children}
    </GlobalSettingsContext.Provider>
  );
};

export const useGlobalSettings = () => {
  const context = useContext(GlobalSettingsContext);

  if (context === undefined) {
    throw new Error("useGlobalSettings must be used within a GlobalSettingsProvider");
  }

  return context;
};
